import React, { useState, useEffect, useRef } from 'react'
import Cover from '../components/Cover'
import CoverHealth from '../components/CoverHealth'
import CoverDiff from '../components/CoverDiff'
import CoverGuide from '../components/CoverGuide'
import OutroText from '../components/OutroText'
import AnswersTable from '../components/AnswersTable'
import AnswersTableDiff from '../components/AnswersTableDiff'
import AnswersTableTeams from '../components/AnswersTableTeams'
import AnswersTableTeamsQuinary from '../components/AnswersTableTeamsQuinary'
import AnswersTablePulse from '../components/AnswersTablePulse'
import AnswersTableGuide from '../components/AnswersTableGuide'
import AnswersTableGuideQuinary from '../components/AnswersTableGuideQuinary'
import AnswersTableHealthQuinary from '../components/AnswersTableHealthQuinary'
import Header from '../components/Header'
import FooterDynamic from '../components/FooterDynamic'
import OverviewGauges from '../components/OverviewGauges'
import OverviewGaugesDiff from '../components/OverviewGaugesDiff'
import OverviewGaugesHealth from '../components/OverviewGaugesHealth'
import OverviewGaugesHealthQuinary from '../components/OverviewGaugesHealthQuinary'
import OverviewGaugesPulse from '../components/OverviewGaugesPulse'
import OverviewTable from '../components/OverviewTable'
import OverviewTableDiff from '../components/OverviewTableDiff'
import OverviewTableTeams from '../components/OverviewTableTeams'
import OverviewTableTeamsQuinary from '../components/OverviewTableTeamsQuinary'
import OverviewTableHealthQuinary from '../components/OverviewTableHealthQuinary'
import OverviewDifference from '../components/OverviewDifference'
import { Document, Page, Font, StyleSheet, BlobProvider } from '@react-pdf/renderer'
import AnswersGuideInfoText from '../components/AnswersGuideInfoText'
import AnswersGuideInfoTextQuinary from '../components/AnswersGuideInfoTextQuinary'
import IntroText from '../components/IntroText'

Font.register({
  family: 'Inter',
  fonts: [
    { src: `${process.env.PUBLIC_URL}/font/Inter-Regular.ttf`, fontWeight: 400 },
    { src: `${process.env.PUBLIC_URL}/font/Inter-Medium.ttf`, fontWeight: 500 },
    { src: `${process.env.PUBLIC_URL}/font/Inter-SemiBold.ttf`, fontWeight: 600 },
    { src: `${process.env.PUBLIC_URL}/font/Inter-Bold.ttf`, fontWeight: 700 }
  ]
})

Font.registerHyphenationCallback(word => [word]);

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
  },
});

const PdfGenerator = () => {

  const [data, setData] = useState({});
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isDataProcessed, setIsDataProcessed] = useState(false);
  const [uniqueId, setUniqueId] = useState(null);
  const [waitingForData, setWaitingForData] = useState(true);
  const wsRef = useRef(null);
  const uploadStartedRef = useRef(false);

  const allowedOrigins = [
    'https://administration.oqm-business.com',
    'https://administration.dev.oqm-business.com',
    'https://survey.oqm-business.com',
    'https://survey.dev.oqm-business.com',
    'https://oqm-pdf-generator-ed22e486ca37.herokuapp.com',
    'http://localhost:3000',
    'http://localhost:3001',
    'http://localhost:3002',
  ];

  const sendBridgeMessage = (payload) => {
    if (window.parent === window) {
      return;
    }

    let parentOrigin = '*';
    try {
      const referrerOrigin = new URL(document.referrer).origin;
      parentOrigin = allowedOrigins.includes(referrerOrigin) ? referrerOrigin : '*';
    } catch (error) {
      parentOrigin = '*';
    }

    window.parent.postMessage(payload, parentOrigin);
  };

  const sendPDFtoServer = async (blobUrl, uniqueId) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();

    const formData = new FormData();

    formData.append("file", blob, "OQM-Business.pdf");
    formData.append("uniqueId", uniqueId);
    console.log("Sending uniqueId:", uniqueId);

    const apiUrl = process.env.NODE_ENV === 'production'
      ? `${window.location.protocol}//${window.location.host}/api/upload-pdf`
      : 'http://localhost:3001/api/upload-pdf';


    try {
      const uploadResponse = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
      });

      if (uploadResponse.ok) {
        console.log("PDF uploaded to server");

        const responseJson = await uploadResponse.json();
        const serverPdfUrl = responseJson.url;

        if (serverPdfUrl) {
          console.log('PDF URL:', serverPdfUrl)
        } else {
          console.error("No URL returned from server");
        }

        sendBridgeMessage({
          type: 'pdfGenerationComplete',
          status: 'done',
          ok: true,
          uniqueId,
          url: serverPdfUrl || null,
        });

        setIsDataProcessed(true);
        setPdfGenerated(false);
        setPdfBlobUrl(null);
        setUniqueId(null);
        setData({});
        setWaitingForData(true);
        uploadStartedRef.current = false;
      } else {
        console.error("Failed to upload PDF");
        sendBridgeMessage({
          type: 'pdfGenerationComplete',
          status: 'error',
          ok: false,
          uniqueId,
          error: 'Failed to upload PDF',
        });
        uploadStartedRef.current = false;
      }
    } catch (error) {
      console.error("Error while uploading PDF", error);
      sendBridgeMessage({
        type: 'pdfGenerationComplete',
        status: 'error',
        ok: false,
        uniqueId,
        error: error?.message || 'Error while uploading PDF',
      });
      uploadStartedRef.current = false;
    }
  };

  const resetStateAndStopWebSocket = () => {
    setData({});
    setPdfGenerated(false);
    setPdfBlobUrl(null);
    setIsDataProcessed(false);
    setUniqueId(null);
    setWaitingForData(true);
    uploadStartedRef.current = false;
    if (wsRef.current) {
      wsRef.current.close();
    }
  };

  useEffect(() => {
    const wsUrl = process.env.NODE_ENV === 'production'
      ? `wss://${window.location.host}`
      : 'ws://localhost:3001';

    const connectWebSocket = () => {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => console.log('WebSocket Connected');

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('Message from server:', message);

          if (message.uniqueId) {
            setUniqueId(message.uniqueId);
            uploadStartedRef.current = false;
          }

          if (message && message.data) {
            setData(message.data);
            setPdfGenerated(true);
            setIsDataProcessed(false);
            setWaitingForData(false);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket Disconnected. Attempting to reconnect...');
        setTimeout(connectWebSocket, 3000);
      };
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (uniqueId) {
      console.log("Unique ID is set, now sending PDF to server:", uniqueId);
    }
  }, [uniqueId]);

  useEffect(() => {
    if (pdfGenerated && !isDataProcessed && uniqueId && pdfBlobUrl && !uploadStartedRef.current) {
      uploadStartedRef.current = true;
      sendPDFtoServer(pdfBlobUrl, uniqueId);
    }
  }, [pdfGenerated, pdfBlobUrl, isDataProcessed, uniqueId]);

  if (waitingForData || !data.info || data.info.length === 0) {
    return <p className='w-full h-full flex justify-center items-center'>Waiting for data...</p>;
  }

  const typePDF = data.pdf;
  const rawCompanyLogo = data.companyLogo || null;
  const quinaryScale = data.quinary;
  const questionsOn = data.inclQuestions;
  const add2GaugePage = !!data.add2GaugePage;
  //const comparisionOn = data.valueComparison;
  const uiData = data.ui[0];
  const coverData = data.info[0];
  const categoriesData = data.categories;
  const answersData = data.answers;
  const hasIntroText = coverData.introText?.trim().length > 0;
  const hasOutroText = coverData.outroText?.trim().length > 0;

  const clientLogo = rawCompanyLogo
    ? `${process.env.NODE_ENV === 'production'
      ? `${window.location.protocol}//${window.location.host}`
      : 'http://localhost:3001'
    }/proxy-logo?url=${encodeURIComponent(rawCompanyLogo)}`
    : null;

  // Adaptor for different JSON structures between Teams & Comparision
  const categoriesDataForDiff = Array.isArray(categoriesData) && categoriesData.length
    ? (Array.isArray(categoriesData[0]?.points)
      ? categoriesData
      : Array.isArray(categoriesData[0]?.profiles)
        ? categoriesData
          .map(c => {
            const profilePoints = Array.isArray(c?.profiles)
              ? c.profiles
                .map(profile => profile?.points)
                .filter(point => point != null)
                .slice(0, 4)
              : [];

            if (profilePoints.length < 2) return null;
            return { ...c, points: profilePoints };
          })
          .filter(Boolean)
        : [])
    : [];

  // Render current report PDF
  const renderAnswerTables = () => {
    return categoriesData.map((category, index) => (
      <Page key={index} size='A4' style={styles.page}>
        <Header />
        <AnswersTable
          answersData={answersData}
          categoriesData={categoriesData}
          uiData={uiData}
          matchCategory={category.name}
          spaceTop='35'
          spaceBottom='35'
        />
        <FooterDynamic uiData={uiData} />
      </Page>
    ));
  };

  const pdfContent = (
    <Document id='html-constructor'>
      <Page size='A4'>
        <Cover coverData={coverData} uiData={uiData} clientLogo={clientLogo} />
      </Page>
      {hasIntroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <IntroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
      <Page size='A4' style={{ backgroundColor: "#f8fafc" }}>
        <Header />
        <OverviewGauges coverData={coverData} categoriesData={categoriesData} uiData={uiData} />
        <FooterDynamic uiData={uiData} />
      </Page>
      <Page size='A4'>
        <Header />
        <OverviewTable coverData={coverData} categoriesData={categoriesData} uiData={uiData} />
        <FooterDynamic uiData={uiData} />
      </Page>
      {renderAnswerTables()}
      <Page size='A4'>
        <Header />
        <AnswersTable answersData={answersData} categoriesData={categoriesData} uiData={uiData} matchCategory={uiData.lowest} spaceTop='42' spaceBottom='42' />
        <AnswersTable answersData={answersData} categoriesData={categoriesData} uiData={uiData} matchCategory={uiData.highest} spaceTop='42' spaceBottom='42' />
        <FooterDynamic uiData={uiData} />
      </Page>
      {hasOutroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <OutroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
    </Document>
  )
  //////////////////////////////////////

  // Render guide PDF

  const AnswersTableGuideComponent = quinaryScale === 1 ? AnswersTableGuideQuinary : AnswersTableGuide;
  const AnswersGuideInfoTextComponent = quinaryScale === 1 ? AnswersGuideInfoTextQuinary : AnswersGuideInfoText;

  const pdfGuide = (
    <Document id='html-constructor'>
      <Page size='A4'>
        <CoverGuide coverData={coverData} uiData={uiData} clientLogo={clientLogo} />
      </Page>
      {hasIntroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <IntroText coverData={coverData} uiData={uiData} typePDF={typePDF} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
      <Page size='A4'>
        <Header />
        <AnswersTableGuideComponent answersData={answersData} categoriesData={categoriesData} uiData={uiData} coverData={coverData} matchCategory={uiData.lowest} spaceTop='42' spaceBottom='42' />
        <FooterDynamic uiData={uiData} />
      </Page>
      <AnswersGuideInfoTextComponent coverData={coverData} answersData={answersData} categoriesData={categoriesData} uiData={uiData} matchCategory={uiData.lowest} />
      {hasOutroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <OutroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
    </Document>
  )
  //////////////////////////////////////

  // Render health report PDF

  const AnswersTableHealthComponent = quinaryScale === 1 ? AnswersTableHealthQuinary : AnswersTable;
  const OverviewTableHealthComponent = quinaryScale === 1 ? OverviewTableHealthQuinary : OverviewTable;
  const OverviewGaugesHealthComponent = quinaryScale === 1 ? OverviewGaugesHealthQuinary : OverviewGaugesHealth;

  const renderAnswerTablesHealth = () => {
    return categoriesData.map((category, index) => (
      <Page key={index} size='A4' style={styles.page}>
        <Header />
        <AnswersTableHealthComponent
          answersData={answersData}
          categoriesData={categoriesData}
          coverData={coverData}
          uiData={uiData}
          matchCategory={category.name}
          spaceTop='35'
          spaceBottom='35'
        />
        <FooterDynamic uiData={uiData} />
      </Page>
    ));
  };

  const pdfContentHealth = (
    <Document id='html-constructor'>
      <Page size='A4'>
        <CoverHealth coverData={coverData} uiData={uiData} clientLogo={clientLogo} />
      </Page>
      {hasIntroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <IntroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
      <Page size='A4' style={{ backgroundColor: "#f8fafc" }}>
        <Header />
        <OverviewGaugesHealthComponent coverData={coverData} categoriesData={categoriesData} uiData={uiData} />
        <FooterDynamic uiData={uiData} />
      </Page>
      <Page size='A4'>
        <Header />
        <OverviewTableHealthComponent coverData={coverData} categoriesData={categoriesData} uiData={uiData} />
        <FooterDynamic uiData={uiData} />
      </Page>
      {renderAnswerTablesHealth()}
      <Page size='A4'>
        <Header />
        <AnswersTableHealthComponent answersData={answersData} categoriesData={categoriesData} coverData={coverData} uiData={uiData} matchCategory={uiData.lowest} spaceTop='42' spaceBottom='42' />
        <AnswersTableHealthComponent answersData={answersData} categoriesData={categoriesData} coverData={coverData} uiData={uiData} matchCategory={uiData.highest} spaceTop='42' spaceBottom='42' />
        <FooterDynamic uiData={uiData} />
      </Page>
      {hasOutroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <OutroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
    </Document>
  )
  //////////////////////////////////////

  // Render comparison PDF
  const renderAnswerTablesDiff = () => {
    return categoriesData.map((category, index) => (
      <Page key={index} size='A4' style={styles.page}>
        <Header />
        <AnswersTableDiff
          answersData={answersData}
          categoriesData={categoriesData}
          uiData={uiData}
          coverData={coverData}
          matchCategory={category.name}
        />
        <FooterDynamic uiData={uiData} />
      </Page>
    ));
  };

  const pdfContentDiff = (
    <Document id='html-constructor'>
      <Page size='A4'>
        <CoverDiff coverData={coverData} uiData={uiData} clientLogo={clientLogo} />
      </Page>
      {hasIntroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <IntroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
      <Page size='A4' style={{ backgroundColor: '#f9fafe' }}>
        <Header />
        <OverviewTableDiff coverData={coverData} uiData={uiData} />
        <FooterDynamic uiData={uiData} />
      </Page>
      <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
        <Header />
        <OverviewGaugesDiff coverData={coverData} categoriesData={categoriesData} uiData={uiData} />
        <FooterDynamic uiData={uiData} />
      </Page>
      <Page size='A4'>
        <Header />
        <OverviewDifference coverData={coverData} categoriesData={categoriesData} uiData={uiData} />
        <FooterDynamic uiData={uiData} />
      </Page>
      {renderAnswerTablesDiff()}
      {hasOutroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <OutroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
    </Document>
  )

  //////////////////////////////////////

  // Render teams PDF
  const renderAnswerTablesTeams = () => {
    const AnswersTableComponent = quinaryScale === 1 ? AnswersTableTeamsQuinary : AnswersTableTeams;
    return categoriesData.map((category, index) => (
      <Page key={index} size="A4" style={styles.page}>
        <Header />
        <AnswersTableComponent
          answersData={answersData}
          categoriesData={categoriesData}
          uiData={uiData}
          questionsOn={questionsOn}
          coverData={coverData}
          matchCategory={category.name}
          spaceTop="30"
          spaceBottom="10"
          add2GaugePage={add2GaugePage}
        />
        <FooterDynamic uiData={uiData} />
      </Page>
    ));
  };

  const pdfContentTeams = (
    <Document id="html-constructor">
      <Page size="A4">
        <CoverDiff coverData={coverData} uiData={uiData} clientLogo={clientLogo} />
      </Page>
      {hasIntroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <IntroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
      <Page size="A4" style={{ backgroundColor: "#f9fafe" }}>
        <Header />
        {quinaryScale === 1 ? (
          <OverviewTableTeamsQuinary coverData={coverData} uiData={uiData} />
        ) : (
          <OverviewTableTeams coverData={coverData} uiData={uiData} />
        )}
        <FooterDynamic uiData={uiData} />
      </Page>
      {add2GaugePage && categoriesDataForDiff.length > 0 && (
        <Page size="A4" wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <OverviewGaugesDiff
            coverData={coverData}
            categoriesData={categoriesDataForDiff}
            uiData={uiData}
            quinaryScale={quinaryScale}
            add2GaugePage={add2GaugePage}
            avgPoint={coverData.overview.avgPoint}
          />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}

      {renderAnswerTablesTeams()}
      {hasOutroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <OutroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
    </Document>
  );

  //////////////////////////////////////

  // Render pulse PDF
  const renderAnswerTablesPulse = () => {
    return categoriesData.map((category, index) => (
      <Page key={index} size='A4' style={styles.page}>
        <Header />
        <AnswersTablePulse
          answersData={answersData}
          categoriesData={categoriesData}
          uiData={uiData}
          matchCategory={category.name}
          spaceTop='35'
          spaceBottom='35'
        />
        <FooterDynamic uiData={uiData} />
      </Page>
    ));
  };

  const pdfContentPulse = (
    <Document id='html-constructor'>
      <Page size='A4'>
        <Cover coverData={coverData} uiData={uiData} clientLogo={clientLogo} />
      </Page>
      {hasIntroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <IntroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
      <Page size='A4' style={{ backgroundColor: "#f8fafc" }}>
        <Header />
        <OverviewGaugesPulse coverData={coverData} categoriesData={categoriesData} uiData={uiData} />
        <FooterDynamic uiData={uiData} />
      </Page>
      {renderAnswerTablesPulse()}
      <Page size='A4'>
        <Header />
        <AnswersTablePulse answersData={answersData} categoriesData={categoriesData} uiData={uiData} matchCategory={uiData.lowest} spaceTop='42' spaceBottom='30' />
        <AnswersTablePulse answersData={answersData} categoriesData={categoriesData} uiData={uiData} matchCategory={uiData.highest} spaceTop='42' spaceBottom='30' />
        <FooterDynamic uiData={uiData} />
      </Page>
      {hasOutroText && (
        <Page size='A4' wrap style={{ backgroundColor: "#f8fafc" }}>
          <Header />
          <OutroText coverData={coverData} />
          <FooterDynamic uiData={uiData} />
        </Page>
      )}
    </Document>
  )
  //////////////////////////////////////

  return (
    <div className='flex min-h-full flex-1 flex-col justify-center items-center'>
      <BlobProvider document={
        typePDF === "current" ? pdfContent :
          typePDF === "health" ? pdfContentHealth :
            typePDF === "comparison" ? pdfContentDiff :
              typePDF === "teams" ? pdfContentTeams :
                typePDF === "guide" ? pdfGuide :
                  pdfContentPulse
      }>
        {({ url, loading, error }) => {
          if (url && !loading && url !== pdfBlobUrl) {
            setPdfBlobUrl(url);
          } else {
            return <p>{loading ? 'Generating PDF...' : 'Error generating PDF'}</p>;
          }
        }}
      </BlobProvider>
    </div>
  )
}

export default PdfGenerator
